import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import {
    CommonPageProps,
    ImageShape,
    NonUndefined,
    PresetFilterField,
    PromiseRejectedResult,
    ReviewItem,
} from '@/types';
import { m } from 'framer-motion';

import DefaultLayout from '@/components/layout/DefaultLayout';
import { getCommonPageProps } from '@/api/common';
import Link from 'next/link';
import { ApiProductsResponse, getProducts } from '@/api/products';
import { CSSProperties, useEffect, useMemo, useState } from 'react';
import { useIsomorphicLayoutEffect } from 'framer-motion';
import { ApiSiteSettingsResponse, getSiteSettings } from '@/api/site-settings';
import CatalogListWithPagination from '@/components/general/CatalogListWithPagination/CatalogListWithPagination';
import CatalogFilters from '@/components/layout/CatalogFilters';
import { CATEGORIES_IDS, CategoryId, getCategory } from '@/api/categories';
import { useFiltersState } from '@/atoms/catalog/filters-state';
import { useSetOpenedPopupsState } from '@/atoms/opened-popups';
import { useSetSelectedServiceValueFormState } from '@/atoms/form/selected-service-value-form-state';
import { FEEDBACK_POPUP_NAME } from '@/components/general/FeedbackPopup/FeedbackPopup';
import useScrollRestoration from '@/hooks/use-scroll-restoration';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import { ApiPageResponse } from '@/api/page';
import { ApiReviewsResponse, getReviews } from '@/api/reviews';
import dynamic from 'next/dynamic';
import { fromCDN } from '@/cdn';
import Button from '@/components/shared/Button';
import Image from 'next/image';
import { useTypograf } from '@/hooks/use-typograf';
import { useSetFeedbackPopupDataNameState } from '@/components/general/FeedbackPopup/use-feedback-popup-data-name-atom';
import { useSetCallbackLinkState } from '@/atoms/catalog-detail/callback-link';
import { AllFilterFields, ApiAllProductFiltersResponse, getAllProductFilters } from '@/api/allProductFilters';
import SecondStep from '@/components/general/CatalogLandingPopup/SecondStep';

export type Category = {
    id: number;
    name: string;
    slug: string;
    image: ImageShape | null;
    lottie: string | null;
    amount: number | null;
};

export const CASUAL_LIMIT = 47;
const CASUAL_TITLE_INDEX = 0;

const ReviewsSlider = dynamic(() => import('@/components/shared/ReviewsSlider'));
const ReviewPopup = dynamic(() => import('@/components/shared/ReviewsSlider/ReviewPopup'));

export type CatalogPageProps = NonUndefined<
    CommonPageProps & {
        h1: string;
        categories: Category[];
        categoryId: CategoryId | null;
        catalogFindProductText: string;
        catalogFindProductButtonText: string;
        catalogAllProductText: string;
        queryState: Record<string, any>;
        initialCasualResponse: ApiProductsResponse;
        reviews: ReviewItem[];
        allFilters: Partial<AllFilterFields>;
        isMenuPage?: boolean;
    }
>;

const CatalogPage = ({
    h1,
    categories,
    categoryId,
    initialCasualResponse,
    catalogFindProductText,
    catalogFindProductButtonText,
    catalogAllProductText,
    queryState,
    allFilters,
    reviews,
    isMenuPage,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
    const [heightDifference, setHeightDifference] = useState(0);
    const [, setFiltersState] = useFiltersState();
    const { openPopup } = useSetOpenedPopupsState();
    const setServiceValueFormState = useSetSelectedServiceValueFormState();
    const setFeedbackPopupDataName = useSetFeedbackPopupDataNameState();
    const router = useRouter();
    useScrollRestoration(router);

    useEffect(() => {
        setFiltersState({
            ...queryState,
            'brands[]': queryState['brands[]'].map((brand: string) => parseInt(brand, 10)),
            'materials[]': queryState['materials[]'].map((material: string) => parseInt(material, 10)),
            'type[]': queryState['type[]'],
            'insert[]': queryState['insert[]'],
            'color[]': queryState['color[]'],
            'collection[]': queryState['collection[]'],
            'dial_color[]': queryState['dial_color[]'],
            'genders[]': queryState['genders[]'],
            'bracelets[]': queryState['bracelets[]'],
            'mechanisms[]': queryState['mechanisms[]'],
            'options[]': queryState['options[]'],
            'shapes[]': queryState['shapes[]'],
            'state[]': queryState['state[]'].map((state: string) => parseInt(state, 10)),
            priceMin: queryState.priceMin ?? undefined,
            priceMax: queryState.priceMax ?? undefined,
            powerMin: queryState.powerMin ?? undefined,
            powerMax: queryState.powerMax ?? undefined,
            diameterMin: queryState.diameterMin ?? undefined,
            diameterMax: queryState.diameterMax ?? undefined,
        });
    }, [queryState, setFiltersState]);

    const tpCatalogFindProductText = useTypograf(catalogFindProductText);
    const tpCatalogFindProductButtonText = useTypograf(catalogFindProductButtonText);

    const [available, setAvailable] = useState(queryState.available);
    const type = available === 1 ? 'available' : available === 0 ? 'not-available' : 'all';
    const setCallbackLink = useSetCallbackLinkState();

    const newPrefilters = useMemo(() => {
        return Object.entries(allFilters.prefilters ?? {}).flatMap(([field, items]) =>
            items.map((item) => ({
                ...item,
                field: field as PresetFilterField,
            })),
        );
    }, [allFilters.prefilters]);

    useEffect(() => {
        setCallbackLink(false);

        return () => {
            setCallbackLink(true);
        };
    }, [setCallbackLink]);

    useEffect(() => {
        if (router.query.available) {
            setAvailable(Number(router.query.available));
        }
    }, [router.query]);

    const sortedCategories = useMemo(() => {
        return categoryId
            ? categories.sort((a: Category, b: Category) => {
                  if (a.id === categoryId) {
                      return -1;
                  }
                  if (b.id === categoryId) {
                      return 1;
                  }

                  return a.id - b.id;
              })
            : categories;
    }, [categoryId, categories]);

    useIsomorphicLayoutEffect(() => {
        const onResize = () => {
            let minHeight = Infinity;
            let maxHeight = -Infinity;
            const cards = document.querySelectorAll<HTMLElement>('.catalog-category-card');

            cards.forEach((card) => {
                const height = card.clientHeight;
                maxHeight = Math.max(maxHeight, height);
                minHeight = Math.min(minHeight, height);
            });
            setHeightDifference(maxHeight - minHeight);
        };
        window.addEventListener('resize', onResize);
        onResize();

        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, []);

    if (isMenuPage) {
        return (
            <DefaultLayout className="parting-page-content">
                <h1 className="visually-hidden">{h1}</h1>
                <m.div className="catalog-landing-description m-text-xs" key="landing-description">
                    <m.div
                        className="clip-container"
                        initial={{ y: '100%' }}
                        animate={{
                            y: 0,
                            transition: {
                                delay: 0.4,
                                duration: 0.4,
                            },
                        }}
                        exit={{
                            y: '-100%',
                            transition: {
                                duration: 0.6,
                            },
                        }}
                    >
                        <m.div
                            initial={{ y: '-100%' }}
                            animate={{
                                y: 0,
                                transition: {
                                    delay: 0.4,
                                    duration: 0.4,
                                },
                            }}
                            exit={{
                                y: '100%',
                                transition: {
                                    duration: 0.6,
                                },
                            }}
                        >
                            Выберите тип товара
                        </m.div>
                    </m.div>
                </m.div>
                <SecondStep categories={categories} classname="catalog-menu-page__menu" isMenuPage={true} />
            </DefaultLayout>
        );
    }

    return (
        <DefaultLayout>
            <div className="wrapper catalog-wrapper">
                <div className="catalog-top">
                    <div className="catalog-top__main">
                        <Button
                            icon={<Image src="/img/back.svg" alt="" unoptimized fill />}
                            iconReverse={true}
                            tag={Link}
                            href="/"
                            geometryVariant="arrow"
                            className="catalog-to-main back-button-clickable"
                        >
                            на главную
                        </Button>
                        <h1 className="catalog-page-title h1">{h1}</h1>
                    </div>
                </div>
                <div
                    className={classNames('catalog-find-product', {
                        'catalog-find-product--with-one-category': sortedCategories.length === 1,
                    })}
                    style={{ '--offset': `${heightDifference}px` } as CSSProperties}
                >
                    <div className="catalog-find-product__text text-l">{tpCatalogFindProductText}</div>
                    <Button
                        geometryVariant="mustache"
                        onClick={() => {
                            setServiceValueFormState(catalogFindProductText);
                            setFeedbackPopupDataName('empty');
                            openPopup(FEEDBACK_POPUP_NAME);
                        }}
                    >
                        {tpCatalogFindProductButtonText}
                    </Button>
                </div>

                {reviews && reviews.length > 0 && <ReviewsSlider sliderTitle="Звездные отзывы" slides={reviews} />}

                <CatalogListWithPagination
                    titleIndex={CASUAL_TITLE_INDEX}
                    title={catalogAllProductText}
                    initialResponse={initialCasualResponse}
                    initialQueryState={queryState}
                    filters={allFilters.filters}
                />
            </div>
            <CatalogFilters
                brands={allFilters.filters?.brands}
                materials={allFilters.filters?.materials}
                filters={allFilters.filters}
                category={categoryId || CATEGORIES_IDS['watch']}
                presetFilters={newPrefilters}
            />
            <ReviewPopup />
        </DefaultLayout>
    );
};

export default CatalogPage;

export const getServerSideProps: GetServerSideProps<CatalogPageProps> = async ({ query }) => {
    const {
        available,
        category,
        page,
        search,
        priceMin,
        priceMax,
        powerMin,
        powerMax,
        diameterMin,
        diameterMax,
        popularity,
        order,
        sort,
    } = query;

    const categoryId = category ?? CATEGORIES_IDS['watch'];
    const brands = query['brands[]'];
    const materials = query['materials[]'];
    const genders = query['genders[]'];
    const bracelets = query['bracelets[]'];
    const mechanisms = query['mechanisms[]'];
    const options = query['options[]'];
    const shapes = query['shapes[]'];
    const type = query['type[]'];
    const color = query['color[]'];
    const insert = query['insert[]'];
    const collection = query['collection[]'];
    const dialColors = query['dial_color[]'];
    const queryState = {
        category: category ? Number(category) : null,
        page: page ? Math.max(1, Number(page)) : 1,
        search: search ? search : null,
        'brands[]': typeof brands === 'string' ? [brands] : brands || [],
        priceMin: priceMin ? Number(priceMin) : null,
        priceMax: priceMax ? Number(priceMax) : null,
        powerMin: powerMin ? Number(powerMin) : null,
        powerMax: powerMax ? Number(powerMax) : null,
        diameterMin: diameterMin ? Number(diameterMin) : null,
        diameterMax: diameterMax ? Number(diameterMax) : null,
        available:
            typeof available === 'string' && (Number(available) === 0 || Number(available) === 1)
                ? (Number(available) as 0 | 1)
                : null,
        popularity:
            typeof popularity === 'string' && (Number(popularity) === 0 || Number(popularity) === 1)
                ? (Number(popularity) as 0 | 1)
                : null,
        'state[]': Array.isArray(query['state[]'])
            ? query['state[]'].map((item) => Number(item))
            : typeof query['state[]'] === 'string'
              ? [Number(query['state[]'])]
              : [],
        'materials[]': typeof materials === 'string' ? [materials] : materials || [],
        'genders[]': typeof genders === 'string' ? [genders] : genders || [],
        'bracelets[]': typeof bracelets === 'string' ? [bracelets] : bracelets || [],
        'mechanisms[]': typeof mechanisms === 'string' ? [mechanisms] : mechanisms || [],
        'options[]': typeof options === 'string' ? [options] : options || [],
        'shapes[]': typeof shapes === 'string' ? [shapes] : shapes || [],
        order: order ? order : null,
        sort: sort ? sort : null,
        'type[]': typeof type === 'string' ? [type] : type || [],
        'color[]': typeof color === 'string' ? [color] : color || [],
        'insert[]': typeof insert === 'string' ? [insert] : insert || [],
        'collection[]': typeof collection === 'string' ? [collection] : collection || [],
        'dial_color[]': typeof dialColors === 'string' ? [dialColors] : dialColors || [],
    };

    const data = await Promise.allSettled([
        getCategory(categoryId as CategoryId),
        getCommonPageProps(),
        getSiteSettings({
            keys: [
                'catalog_title',
                'catalog_find_product_text',
                'catalog_find_product_button_text',
                'catalog_all_product_text',
            ],
        }),
        getProducts({
            params: {
                limit: CASUAL_LIMIT,
                page: queryState.page || undefined,
                category: queryState.category !== null ? queryState.category : CATEGORIES_IDS['watch'],
                available: queryState.available || undefined,
                sort: (queryState.sort as string) || undefined,
                order: (queryState.order as string) || undefined,
                'materials[]': queryState['materials[]'].map((item) => Number(item)),
                'genders[]': queryState['genders[]'].map((item) => Number(item)),
                'bracelet[]': queryState['bracelets[]'],
                'mechanism[]': queryState['mechanisms[]'],
                'collection[]': queryState['collection[]'].map((item) => item),
                'dial_color[]': queryState['dial_color[]'].map((item) => item),
                priceMin: queryState.priceMin || undefined,
                priceMax: queryState.priceMax || undefined,
                powerMin: queryState.powerMin || undefined,
                powerMax: queryState.powerMax || undefined,
                diameterMin: queryState.diameterMin || undefined,
                diameterMax: queryState.diameterMax || undefined,
                'state[]': Array.isArray(query['state[]'])
                    ? query['state[]'].map((item) => Number(item))
                    : typeof query['state[]'] === 'string'
                      ? [Number(query['state[]'])]
                      : undefined,
                'brands[]': queryState['brands[]'].map((item) => Number(item)),
                'type[]': queryState['type[]'],
                'color[]': queryState['color[]'],
                'insert[]': queryState['insert[]'],
                'options[]': queryState['options[]'],
                'shapes[]': queryState['shapes[]'],
            },
        }),
        getAllProductFilters({
            params: {
                category: typeof queryState.category === 'number' ? queryState.category : CATEGORIES_IDS['watch'],
            },
        }),
        getReviews(),
    ]);

    const [categoryInfo, commonPageProps, siteSettings, initialCasualResponse, allFiltersResponse, reviewsResponse] =
        data;

    if (categoryInfo.status === 'rejected') {
        return { notFound: true };
    }

    const isMenuPage =
        initialCasualResponse.status === 'fulfilled' &&
        ((!query.category && !query.sort && !query.order) ||
            (Array.isArray(initialCasualResponse.value.data?.items) &&
                initialCasualResponse.value.data?.items?.length === 0));

    if (data.every((result) => result.status === 'fulfilled')) {
        const commonPagePropsSettled = commonPageProps as PromiseFulfilledResult<CommonPageProps>;
        const pageInfoSettled = categoryInfo as PromiseFulfilledResult<ApiPageResponse>;
        const siteSettingsSettled = siteSettings as PromiseFulfilledResult<ApiSiteSettingsResponse>;
        const initialCasualResponseSettled = initialCasualResponse as PromiseFulfilledResult<ApiProductsResponse>;
        const allFiltersResponseSettled = allFiltersResponse as PromiseFulfilledResult<ApiAllProductFiltersResponse>;
        const reviewsResponseSettled = reviewsResponse as PromiseFulfilledResult<ApiReviewsResponse>;

        return {
            props: {
                ...commonPagePropsSettled.value,
                bodyClass: 'catalog-page',
                meta: {
                    ...commonPagePropsSettled.value.meta,
                    title: 'Каталог',
                    ...pageInfoSettled.value.data?.meta,
                },
                h1: siteSettingsSettled.value.data?.catalog_title ?? 'Каталог',
                categories: commonPagePropsSettled.value.categories || [],
                categoryId: typeof category === 'string' ? (parseInt(category, 10) as CategoryId) : null,
                catalogFindProductText: siteSettingsSettled.value.data?.catalog_find_product_text ?? '',
                catalogFindProductButtonText: siteSettingsSettled.value.data?.catalog_find_product_button_text ?? '',
                catalogAllProductText: siteSettingsSettled.value.data?.catalog_all_product_text ?? '',
                queryState,
                initialCasualResponse: initialCasualResponseSettled.value,
                course: commonPagePropsSettled.value.course,
                reviews:
                    reviewsResponseSettled.value?.data?.map((item) => {
                        return {
                            ...item,
                            video: (item.video || []).map((video) => {
                                return {
                                    type: video.type,
                                    src: fromCDN(video.src),
                                };
                            }),
                        };
                    }) || [],
                allFilters: allFiltersResponseSettled.value.data ?? {},
                isMenuPage,
            } satisfies CatalogPageProps,
        };
    }

    for (const result of data) {
        if (result.status === 'rejected') {
            const rejected = result as PromiseRejectedResult;
            console.error('SSR fetch failed:', rejected.reason);

            if (rejected.reason?.response?.status === 404) {
                return { notFound: true };
            }

            if (rejected.reason instanceof Error) {
                throw rejected.reason;
            }
        }
    }

    throw new Error('Internal server error');
};
